import { useId, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '@ds/core';
import { Alert } from '@ds/feedback';
import { FormField, Input } from '@ds/forms';
import { definirNovaSenha, pedirRecuperacao } from '@/session/sessao';

/**
 * O link do e-mail volta para esta mesma rota com os tokens no fragmento
 * (`#access_token=…&type=recovery`), e é o cliente do Supabase que os troca por
 * sessão ao carregar. Então a tela tem dois momentos, e não duas rotas: pedir o
 * e-mail e, de volta do link, definir a senha.
 */
function veioDoLink(): boolean {
    if (typeof window === 'undefined') return false;
    const hash = window.location.hash ?? '';
    return hash.includes('type=recovery') || hash.includes('access_token');
}

export function RecuperarSenha() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [confirmacao, setConfirmacao] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [enviado, setEnviado] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [redefinindo] = useState(veioDoLink);

    const idEmail = useId();
    const idSenha = useId();
    const idConfirmacao = useId();

    const pedir = async () => {
        setErro(null);
        setEnviando(true);
        /* O destino é esta mesma tela: é para cá que o link do e-mail volta. */
        const r = await pedirRecuperacao(email, `${window.location.origin}/recuperar-senha`);
        setEnviando(false);
        if (!r.ok) {
            setErro(r.erro ?? 'Não foi possível enviar o e-mail.');
            return;
        }
        setEnviado(true);
    };

    const redefinir = async () => {
        setErro(null);
        if (senha !== confirmacao) {
            setErro('As duas senhas não são iguais.');
            return;
        }
        setEnviando(true);
        const r = await definirNovaSenha(senha);
        setEnviando(false);
        if (!r.ok) {
            setErro(r.erro ?? 'Não foi possível salvar a senha.');
            return;
        }
        navigate('/entrar', { replace: true });
    };

    if (redefinindo) {
        return (
            <form
                onSubmit={(e) => { e.preventDefault(); void redefinir(); }}
                style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
                <div>
                    <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>Definir nova senha</h1>
                    <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--text-secondary)' }}>
                        O link do e-mail vale uma vez e por tempo limitado.
                    </p>
                </div>

                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                <FormField label="Nova senha" htmlFor={idSenha} hint="Pelo menos 8 caracteres." required>
                    <Input id={idSenha} type="password" name="senha" autoComplete="new-password" value={senha} onChange={setSenha} />
                </FormField>

                <FormField label="Repita a nova senha" htmlFor={idConfirmacao} required>
                    <Input id={idConfirmacao} type="password" name="confirmacao" autoComplete="new-password" value={confirmacao} onChange={setConfirmacao} />
                </FormField>

                <Button type="submit" fullWidth loading={enviando}>Salvar e entrar</Button>
            </form>
        );
    }

    return (
        <form
            onSubmit={(e) => { e.preventDefault(); void pedir(); }}
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
            <div>
                <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>Recuperar senha</h1>
                <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--text-secondary)' }}>
                    Enviamos um link para o e-mail cadastrado.
                </p>
            </div>

            {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

            {enviado ? (
                /* A mesma resposta exista ou não a conta. Dizer "não
                   encontramos este e-mail" transformaria a tela num verificador
                   de quem tem cadastro na clínica. */
                <Alert tone="success" dismissible={false} title="Se houver conta com este e-mail, o link já saiu">
                    Confira a caixa de entrada de <strong>{email}</strong>. O link vale uma vez.
                </Alert>
            ) : (
                <FormField label="E-mail" htmlFor={idEmail} required>
                    <Input id={idEmail} type="email" name="email" autoComplete="username" value={email} onChange={setEmail} placeholder="voce@clinicavecchia.com.br" />
                </FormField>
            )}

            {!enviado && <Button type="submit" fullWidth loading={enviando}>Enviar link</Button>}

            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', textAlign: 'center' }}>
                <Link to="/entrar" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>Voltar para a entrada</Link>
            </p>
        </form>
    );
}
